import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";

/**
 * Search SpecTree people by name or email for the Toolbox role screen (epic
 * PI-SPEC-SW-E283, feature PI-SPEC-SW-1431).
 *
 * `GET /api/v1/users` (any signed-in person) pages through every user but has
 * no search parameter, so the directory is read once (100 per page, every
 * page) and filtered here. SpecTree has tens of people, so one cached read is
 * cheaper and simpler than a server-side search.
 */

/** One person as the role screen needs them. */
export interface DirectoryUser {
  id: string;
  name: string | null;
  email: string;
  isActive: boolean;
}

interface UsersPage {
  data: DirectoryUser[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

/** The most pages read, as a guard against a misbehaving server (100 people per page). */
const MAX_PAGES = 20;
const PAGE_SIZE = 100;

/** At most this many search results are shown. */
export const MAX_USER_RESULTS = 8;

export const usersSearchKeys = {
  all: ["users-directory"] as const,
  directory: () => [...usersSearchKeys.all, "all"] as const,
};

/** Read every SpecTree user, page by page. */
export async function fetchUserDirectory(): Promise<DirectoryUser[]> {
  const users: DirectoryUser[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await api.get<UsersPage>(`/users?page=${String(page)}&limit=${String(PAGE_SIZE)}`);
    users.push(...response.data);
    const totalPages = response.pagination?.totalPages ?? 1;
    if (page >= totalPages || response.data.length === 0) break;
  }
  return users;
}

/**
 * Active people whose name or email contains every word of `query`
 * (case-insensitive), at most `limit`, people in `exclude` left out. An
 * empty query matches nobody. Pure; exported for tests.
 */
export function searchUsers(
  users: readonly DirectoryUser[],
  query: string,
  { exclude = [], limit = MAX_USER_RESULTS }: { exclude?: readonly string[]; limit?: number } = {}
): DirectoryUser[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const skip = new Set(exclude);
  return users
    .filter((user) => user.isActive && !skip.has(user.id))
    .filter((user) => {
      const haystack = `${user.name ?? ""} ${user.email}`.toLowerCase();
      return words.every((word) => haystack.includes(word));
    })
    .slice(0, limit);
}

/** Every SpecTree user, read once and cached for five minutes; `enabled` false skips the read. */
export function useUserDirectory(enabled = true) {
  return useQuery({
    queryKey: usersSearchKeys.directory(),
    queryFn: fetchUserDirectory,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** People matching `query` (see {@link searchUsers}); the directory is read on first use. */
export function useUsersSearch(query: string, options: { exclude?: readonly string[]; enabled?: boolean } = {}) {
  const directory = useUserDirectory(options.enabled ?? true);
  return {
    results: directory.data ? searchUsers(directory.data, query, { exclude: options.exclude ?? [] }) : [],
    isLoading: directory.isLoading,
    isError: directory.isError,
  };
}
